import { Suspense, lazy, useEffect, useState } from 'react';

import cn from 'classnames';
import { MESSAGE_STATE_SUCCESS } from 'constants/message-app-state-contants';
import { useDocumentTitle } from 'hooks/document-title';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, FormMessage, Input, InputGroup } from 'components/FormComponents';
import { Loading } from 'components/Loading/Loading';

import { messagePopUp } from 'api/app-slice';
import { useResetUserPasswordMutation } from 'api/user-api';

import styles from './ResetPassword.module.scss';

const PasswordStrengthBar = lazy(() => import('react-password-strength-bar'));

const ResetPassword = ({ invitation }) => {
  useDocumentTitle('Reset Password');

  const { t } = useTranslation();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const [resetPassword] = useResetUserPasswordMutation();

  const [loading, setLoading] = useState();

  const [error, setError] = useState();
  const [submitError, setSubmitError] = useState();
  const [scorePass, setScorePass] = useState(false);

  const { token } = useParams();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm();

  const watchPassword = useWatch({ control, name: 'password' });
  const watchConfirmPassword = useWatch({ control, name: 'confirm_password' });

  useEffect(() => {
    if (!watchPassword || !watchConfirmPassword) return;
    if (watchPassword !== watchConfirmPassword) setError('Passwords need to match');
    else if (!scorePass) setError('Password is not strong enough');
    else setError(null);
  }, [watchConfirmPassword, watchPassword, scorePass]);

  const submitErrorMessage = (response, requestError) => {
    if (response?.error) return response.error.message;
    if (requestError.status === 400) return requestError.data?.error?.log;
    if (requestError.status === 401) return t('resetPassword.linkInvalid');
    return t('resetPassword.updateFailed');
  };

  const onSubmit = async data => {
    if (error) return;
    setLoading(true);
    const { data: response, error: requestError } = await resetPassword({
      token,
      password: data.password,
      verified: invitation,
    });
    setLoading(false);

    if (requestError || response?.error) {
      setSubmitError(submitErrorMessage(response, requestError));
      return;
    }

    dispatch(
      messagePopUp({
        text: 'Password successfully updated',
        state: MESSAGE_STATE_SUCCESS,
        hide: true,
      }),
    );
    navigate('/sign_in');
  };

  return (
    <div className={styles.root}>
      <Loading active={loading} />
      <div className={cn('card u-margin-top', 'card-with-shadow', styles.card)}>
        <div className={cn('card_content', styles.content)}>
          <img
            onClick={() => window.location.replace('https://www.checkmate.tech')}
            className={cn(styles.logo, 'u-margin-bottom--large')}
            src="/assets/images/checkmate/icon-label.svg"
            alt="KnowMy Logo"
          />
          <h2 className="design-marker title-2 u-margin-bottom--large">
            Welcome{!invitation && <span> back</span>}!
          </h2>
          <form className="u-width-100" onSubmit={handleSubmit(onSubmit)}>
            <FormMessage
              message={submitError}
              className="u-margin-top--0"
              dataTestId="reset-password-error-message"
              onClose={() => setSubmitError(null)}
            />
            <InputGroup title={invitation ? 'Create password' : 'New password'}>
              <Input
                name="password"
                icon="lock"
                type="password"
                placeholder="Enter your password"
                dataTestId="reset-password-password-input"
                register={register}
                validators={{ required: true }}
                error={errors?.password?.type}
                inputProps={{ tabIndex: 2 }}
              />
              <Suspense fallback={<div>Loading password strength...</div>}>
                <PasswordStrengthBar
                  password={watchPassword}
                  onChangeScore={score => {
                    score > 2 ? setScorePass(true) : setScorePass(false);
                  }}
                />
              </Suspense>
            </InputGroup>
            <InputGroup title={invitation ? 'Confirm password' : 'Confirm New password'}>
              <Input
                name="confirm_password"
                icon="lock"
                type="password"
                placeholder="Re-Enter your password"
                dataTestId="reset-password-confirm-password-input"
                register={register}
                validators={{ required: true }}
                error={error}
                inputProps={{ tabIndex: 2 }}
              />
            </InputGroup>
            <Button
              submit
              buttonProps={{ tabIndex: 3 }}
              className="u-width-100 u-margin-top--large"
              dataTestId="reset-password-submit-button">
              Reset Password
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
